import { TrashIcon } from "@heroicons/react/solid";
import React, { useCallback, useMemo } from "react";
import SearchableColumnSelect from "../../shared/SearchableColumnSelect";

const filterOperators = {
  single_select: ["is", "is not", "is not empty", "is empty"],
  multi_select: ["includes", "does not include", "is not empty", "is empty"],
  household_link: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
  link: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
  claim_household: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
  text: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
  checkbox: ["checked", "not checked"],
  date: [
    "is not empty",
    "is empty",
    "is",
    "is not",
    "is before",
    "is after",
    "is on or before",
    "is on or after",
  ],
  due_date: ["is not empty", "is empty", "is", "is not", "is before", "is after", "is on or before", "is on or after"],
  number: ["is not empty", "is empty", "<", ">", "=", '<=', '>=', '!='],
  advisor: ["is not empty", "is empty", "is", "is not"],
};

const comparisonTextFilters = ['contains', 'does not contain', 'is exactly', 'is not exactly'];
const comparisonDateFilters = ["is", "is not", "is before", "is after", "is on or before", "is on or after"];
const comparisonNumberFilters = ["<", ">", "=", '<=', '>=', '!='];
const comparisonSelectFilters = ["is", "is not", "includes", "does not include"];

// Date comparison options
const dateComparisonOptions = [
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "yesterday", label: "Yesterday" },
  { value: "one_week_ago", label: "One week ago" },
  { value: "one_week_from_now", label: "One week from now" },
  { value: "one_month_ago", label: "One month ago" },
  { value: "one_month_from_now", label: "One month from now" },
  { value: "number_of_days_ago", label: "Number of days ago" },
  { value: "number_of_days_from_now", label: "Number of days from now" },
  { value: "exact_date", label: "Exact date" },
];

const SmartTableFiltersRow = ({
  filters,
  columns,
  filter,
  advisors,
  thisFilterIndex,
  groupOperator,
  isInGroup = false,
}) => {
  // Determine what type of comparison input to show
  const comparisonType = useMemo(() => {
    // For advisor, single_select, and multi_select columns, only show comparison for specific operators
    if (filter.col_type === "advisor" || filter.col_type === "single_select") {
      return ["is", "is not"].includes(filter.operator) ? "select" : null;
    }

    if (filter.col_type === "multi_select") {
      return ["includes", "does not include"].includes(filter.operator) ? "select" : null;
    }

    if (comparisonTextFilters.includes(filter.operator)) return "text";
    if (comparisonDateFilters.includes(filter.operator)) return "date";
    if (comparisonNumberFilters.includes(filter.operator)) return "number";
    if (comparisonSelectFilters.includes(filter.operator)) return "select";
    return null;
  }, [filter.operator, filter.col_type]);

  // Parse the comparison value for date operations
  const parsedDateComparison = useMemo(() => {
    if (!filter.comparison || !comparisonDateFilters.includes(filter.operator)) {
      return { type: null, value: '' };
    }

    const comparisonStr = String(filter.comparison);

    // Handle the new format: "type:value" for days operations
    if (comparisonStr.includes(':') && (comparisonStr.startsWith('number_of_days_ago:') || comparisonStr.startsWith('number_of_days_from_now:'))) {
      const [type, value] = comparisonStr.split(':');
      return { type, value };
    }

    // Check if it looks like an ISO date string (exact date stored without prefix)
    if (comparisonStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return { type: 'exact_date', value: comparisonStr };
    }

    // Check if it's the exact_date placeholder
    if (comparisonStr === 'exact_date') {
      return { type: 'exact_date', value: '' };
    }

    // Check if it's one of our other predefined options
    const predefinedOption = dateComparisonOptions.find(opt => opt.value === comparisonStr);
    if (predefinedOption) {
      return { type: comparisonStr, value: '' };
    }

    // Legacy support - assume it's an exact date
    return { type: 'exact_date', value: comparisonStr };
  }, [filter.comparison, filter.operator]);

  const { type: dateComparisonType, value: dateComparisonValue } = parsedDateComparison;

  const handleUpdateRow = useCallback(async ({ new_value: newValue, key }) => {
    const newFilter = { ...filter };
    const matchingColumn = columns.find(
      (col) => col.col_id === (key === "col_id" ? newValue : newFilter.col_id)
    );

    switch (key) {
      case "col_id":
        newFilter.col_id = newValue;
        newFilter.col_type = matchingColumn?.col_type;
        newFilter.select_options = matchingColumn?.select_options;
        // Reset operator and comparison when column changes
        if (matchingColumn?.col_type) {
          const availableOperators = filterOperators[matchingColumn.col_type];
          newFilter.operator = availableOperators?.[0] || "is not empty";
          newFilter.comparison = null;
        }
        break;
      case "operator":
        newFilter.operator = newValue;
        // Reset comparison when operator changes to one that doesn't need comparison
        if (!comparisonTextFilters.includes(newValue) &&
          !comparisonDateFilters.includes(newValue) &&
          !comparisonNumberFilters.includes(newValue) &&
          !comparisonSelectFilters.includes(newValue)) {
          newFilter.comparison = null;
        }
        break;
      case "comparison":
        newFilter.comparison = newValue;
        break;
      default:
        console.warn(`Unknown filter update key: ${key}`);
        break;
    }

    try {
      filters.update(thisFilterIndex, newFilter);
    } catch (error) {
      console.error('Failed to update filter:', error);
    }
  }, [filter, columns, filters, thisFilterIndex]);

  // Helper function to update date comparison
  const handleDateComparisonUpdate = useCallback((type, value = '') => {
    let comparisonValue;

    // For predefined options without additional values
    if (['today', 'tomorrow', 'yesterday', 'one_week_ago', 'one_week_from_now', 'one_month_ago', 'one_month_from_now'].includes(type)) {
      comparisonValue = type;
    }
    // For number of days operations that need the type:value format
    else if (['number_of_days_ago', 'number_of_days_from_now'].includes(type)) {
      comparisonValue = value ? `${type}:${value}` : type;
    }
    // For exact dates, store the ISO date directly, or just "exact_date" if no date yet
    else if (type === 'exact_date') {
      comparisonValue = value || 'exact_date';
    }
    else {
      comparisonValue = type;
    }

    handleUpdateRow({ new_value: comparisonValue, key: "comparison" });
  }, [handleUpdateRow]);

  const handleRemoveRow = useCallback(async () => {
    try {
      filters.remove(thisFilterIndex);
    } catch (error) {
      console.error('Failed to remove filter:', error);
    }
  }, [filters, thisFilterIndex]);

  const nonFilterableColumns = ["button", "copy_to_clipboard"];

  // Determine the connector text
  const connectorText = useMemo(() => {
    if (thisFilterIndex === 0) return "Where";
    const operator = groupOperator || "AND";
    return operator === "OR" ? "Or" : "And";
  }, [thisFilterIndex, groupOperator]);

  // Format date value for display in date input
  const formatDateForInput = useCallback((dateValue) => {
    if (!dateValue) return '';
    try {
      const dateStr = String(dateValue);
      if (dateStr.includes('T')) {
        return dateStr.split('T')[0];
      }
      return dateStr.substring(0, 10);
    } catch (e) {
      return '';
    }
  }, []);

  // Handle date input changes
  const handleDateInputChange = useCallback((e) => {
    const dateStr = e.target.value;
    if (!dateStr) {
      handleDateComparisonUpdate(dateComparisonType, '');
      return;
    }

    const [year, month, day] = dateStr.split('-').map(Number);
    // Validate the date components
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return;
    }

    try {
      const isoDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0)).toISOString();
      handleDateComparisonUpdate(dateComparisonType, isoDate);
    } catch (e) {
      console.error('Date conversion error:', e);
    }
  }, [dateComparisonType, handleDateComparisonUpdate]);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-stretch divide-x divide-gray-200">
        {/* Filter Label */}
        <div className="px-1 flex items-center justify-center min-w-[60px] rounded-l-lg bg-gray-50">
          <p className="text-xs font-medium text-gray-700">
            {connectorText}
          </p>
        </div>

        {/* Column Selection */}
        <div className="flex-1 min-w-[200px] overflow-visible">
          <SearchableColumnSelect
            columns={columns || []}
            value={filter.col_id}
            onChange={(colId) => handleUpdateRow({ new_value: colId, key: "col_id" })}
            excludeTypes={nonFilterableColumns}
            placeholder="Select column to filter..."
            className="border-none shadow-none rounded-none hover:bg-transparent focus:bg-transparent focus:ring-0 focus:border-transparent text-sm"
          />
        </div>

        {/* Operator Selection */}
        <div className="flex-1 min-w-[140px] overflow-visible">
          <select
            name="operator_options"
            id="operator_options"
            value={filter.operator || ""}
            onChange={(e) => handleUpdateRow({ new_value: e.target.value, key: "operator" })}
            className="w-full h-full  bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
          >
            {filterOperators[filter.col_type]?.map((operator) => (
              <option value={operator} key={operator}>
                {operator}
              </option>
            ))}
          </select>
        </div>

        {/* Comparison Value */}
        {comparisonType && (
          <div className="flex-1 min-w-[200px] px-3 overflow-visible">
            {comparisonType === "date" ? (
              <div className="space-y-2">
                {/* Date comparison type selector */}
                <select
                  value={dateComparisonType || ""}
                  onChange={(e) => {
                    const newType = e.target.value;
                    handleDateComparisonUpdate(newType, '');
                  }}
                  className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                >
                  <option value="">Select comparison</option>
                  {dateComparisonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {/* Additional input for number of days */}
                {(dateComparisonType === 'number_of_days_ago' || dateComparisonType === 'number_of_days_from_now') && (
                  <input
                    type="number"
                    value={dateComparisonValue || ''}
                    onChange={(e) => {
                      handleDateComparisonUpdate(dateComparisonType, e.target.value);
                    }}
                    placeholder="Enter number of days"
                    className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                    min="0"
                  />
                )}

                {/* Date picker for exact date */}
                {dateComparisonType === 'exact_date' && (
                  <input
                    type="date"
                    value={formatDateForInput(dateComparisonValue)}
                    onChange={handleDateInputChange}
                    className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                  />
                )}
              </div>
            ) : comparisonType !== "select" ? (
              <input
                type={comparisonType || "text"}
                value={filter.comparison || ''}
                onChange={(e) => {
                  handleUpdateRow({ new_value: e.target.value, key: "comparison" });
                }}
                placeholder="Enter a value"
                className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
              />
            ) : (
              <select
                value={filter.comparison || ""}
                onChange={(e) => {
                  handleUpdateRow({ new_value: e.target.value, key: "comparison" });
                }}
                className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
              >
                <option value="">Please Select</option>
                {filter.col_type === "advisor" &&
                  advisors?.map((advisor) => (
                    <option key={advisor.user_id} value={advisor.user_id}>
                      {advisor.first_name} {advisor.last_name}
                    </option>
                  ))}
                {(filter.col_type === "single_select" || filter.col_type === 'multi_select') &&
                  filter.select_options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
              </select>
            )}
          </div>
        )}

        {/* Delete Button */}
        <div className="flex items-center">
          <button
            onClick={handleRemoveRow}
            className="px-3  text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 h-full"
            title="Remove filter condition"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SmartTableFiltersRow);
